// src/pages/inventory/VendorReturn.js
// ZENO ERP — Vendor Return
// Returns over-received / wrong / damaged items back to vendor
// Only for grn_locked POs — material already received

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection, getDocs, query, where, addDoc, serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import {
  validateAvailableStock,
  updateInventorySummary,
  createInventoryTransaction,
  MOVEMENT_TYPES,
} from "../../services/inventoryService";

const RETURN_REASONS = [
  "Wrong Item Delivered",
  "Damaged on Delivery",
  "Over Received",
  "Quality Issue",
  "Other",
];

export default function VendorReturn() {
  const navigate  = useNavigate();
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";

  const [pos,             setPos]             = useState([]);
  const [selectedPO,      setSelectedPO]      = useState(null);
  const [lineItems,       setLineItems]        = useState([]);
  const [reason,          setReason]           = useState("");
  const [notes,           setNotes]            = useState("");
  const [loading,         setLoading]          = useState(false);
  const [fetchingPOs,     setFetchingPOs]       = useState(true);

  // ── Fetch grn_locked POs only ──────────────────────────
  useEffect(() => {
    if (!companyId) return;
    fetchPOs();
  }, []);

  const fetchPOs = async () => {
    try {
      setFetchingPOs(true);
      const snap = await getDocs(query(
        collection(db, "purchase_orders"),
        where("company_id",  "==", companyId),
        where("grn_locked",  "==", true),
      ));
      // D-295: Direct Site Purchase stock is at the SITE, not the central
      // store — returning it here would deduct central stock wrongly.
      setPos(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(po => po.purchase_mode !== "direct_site_purchase")
      );
    } catch (err) {
      console.error("fetchPOs error:", err);
    } finally {
      setFetchingPOs(false);
    }
  };

  // ── Select PO → load items ─────────────────────────────
  const handlePOSelect = (poId) => {
    const po = pos.find(p => p.id === poId);
    if (!po) { setSelectedPO(null); setLineItems([]); return; }
    setSelectedPO(po);
    setLineItems(
      (po.items || [])
        .filter(item => !item._cancelled && Number(item.received_qty || 0) > 0)
        .map(item => ({
          item_id:       item.item_id   || "",
          item_code:     item.item_code || "",
          item_name:     item.item_name || "",
          category:      item.category  || "",
          unit:          item.unit      || "nos",
          received_qty:  Number(item.received_qty || 0),
          return_qty:    0,
          rate:          Number(item.rate || 0),
        }))
    );
  };

  const updateReturnQty = (index, value) => {
    const updated = [...lineItems];
    updated[index].return_qty = Math.max(0, Number(value) || 0);
    setLineItems(updated);
  };

  // ── Generate VRT number ────────────────────────────────
  const generateVRTNo = async () => {
    const snap = await getDocs(query(
      collection(db, "vendor_returns"),
      where("company_id", "==", companyId)
    ));
    return `VRT-${String(snap.size + 1).padStart(4, "0")}`;
  };

  // ── Save ───────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedPO)          { alert("Select a Purchase Order"); return; }
    if (!reason.trim())       { alert("Select a return reason"); return; }

    const validItems = lineItems.filter(i => Number(i.return_qty) > 0);
    if (!validItems.length)   { alert("Enter return qty for at least one item"); return; }

    // Validate return qty ≤ received qty
    for (const item of validItems) {
      if (item.return_qty > item.received_qty) {
        alert(`${item.item_name}: Return qty (${item.return_qty}) cannot exceed received qty (${item.received_qty})`);
        return;
      }
      // Validate stock available
      const validation = await validateAvailableStock(
        item.item_id, item.return_qty, companyId
      );
      if (!validation.success) {
        alert(`${item.item_name}: ${validation.message}`);
        return;
      }
    }

    setLoading(true);
    try {
      const vrtNo      = await generateVRTNo();
      const totalAmount = validItems.reduce(
        (sum, i) => sum + (i.return_qty * i.rate), 0
      );

      // ── Save vendor_returns doc ──
      await addDoc(collection(db, "vendor_returns"), {
        company_id:           companyId,
        vendor_return_no:     vrtNo,
        purchase_order_id:    selectedPO.id,
        po_number:            selectedPO.po_number || "",
        vendor_id:            selectedPO.vendor_id || "",
        vendor_name:          selectedPO.vendor_name || "",
        items:                validItems,
        total_amount:         totalAmount,
        return_reason:        reason.trim(),
        notes:                notes.trim(),
        created_by:           user.uid || user.id || "",
        created_by_name:      user.name || "",
        created_at:           serverTimestamp(),
        status:               "completed",
      });

      // ── Inventory engine — deduct stock + ledger entry ──
      for (const item of validItems) {
        await updateInventorySummary({
          company_id:   companyId,
          item_id:      item.item_id,
          item_code:    item.item_code,
          item_name:    item.item_name,
          category:     item.category,
          unit:         item.unit,
          stock_change: -Number(item.return_qty),
        });

        await createInventoryTransaction({
          company_id:     companyId,
          movement_type:  MOVEMENT_TYPES.VENDOR_RETURN,
          item_id:        item.item_id,
          item_code:      item.item_code,
          item_name:      item.item_name,
          category:       item.category,
          qty:            Number(item.return_qty),
          unit:           item.unit,
          stock_effect:   -Number(item.return_qty),
          reference_id:   vrtNo,
          reference_type: "vendor_return",
          remarks:        `${reason}${notes ? " | " + notes : ""}`,
        });
      }

      alert(`✅ Vendor Return ${vrtNo} saved successfully`);
      setSelectedPO(null);
      setLineItems([]);
      setReason("");
      setNotes("");

    } catch (err) {
      console.error("VendorReturn save error:", err);
      alert(err.message || "Failed to save vendor return");
    } finally {
      setLoading(false);
    }
  };

  const totalReturnAmount = lineItems.reduce(
    (sum, i) => sum + (Number(i.return_qty) * Number(i.rate)), 0
  );

  return (
    <div className="p-4 pb-20 bg-gray-50 min-h-screen max-w-3xl mx-auto">

      <button onClick={() => navigate(-1)}
        className="text-sm text-slate-500 hover:text-slate-800 mb-4 block">
        ← Back
      </button>

      <h1 className="text-xl font-bold text-slate-800 mb-5">Vendor Return</h1>

      {/* SELECT PO */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mb-4">
        <label className="text-xs font-semibold text-slate-500 uppercase mb-2 block">
          Select Purchase Order (GRN Completed)
        </label>
        {fetchingPOs ? (
          <p className="text-sm text-slate-400">Loading POs...</p>
        ) : (
          <select
            value={selectedPO?.id || ""}
            onChange={e => handlePOSelect(e.target.value)}
            className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-purple-400 bg-white">
            <option value="">— Select PO —</option>
            {pos.map(po => (
              <option key={po.id} value={po.id}>
                {po.po_number || po.id} — {po.vendor_name || ""}
              </option>
            ))}
          </select>
        )}
        {pos.length === 0 && !fetchingPOs && (
          <p className="text-xs text-slate-400 mt-2">
            No completed GRN orders found. Only GRN-locked POs can have vendor returns.
          </p>
        )}
      </div>

      {/* PO INFO */}
      {selectedPO && (
        <div className="bg-purple-50 border border-purple-100 rounded-2xl p-4 mb-4">
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">PO Number</span>
            <span className="font-semibold">{selectedPO.po_number || selectedPO.id}</span>
          </div>
          <div className="flex justify-between text-sm mt-1">
            <span className="text-slate-500">Vendor</span>
            <span className="font-semibold">{selectedPO.vendor_name}</span>
          </div>
          <div className="flex justify-between text-sm mt-1">
            <span className="text-slate-500">GRN Status</span>
            <span className="font-semibold text-green-600">
              {selectedPO.po_status || "completed"} ✓
            </span>
          </div>
        </div>
      )}

      {/* RETURN ITEMS */}
      {lineItems.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mb-4">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-3">
            Items to Return
          </p>
          <div className="space-y-3">
            {lineItems.map((item, i) => (
              <div key={i} className="border border-slate-100 rounded-xl p-3">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-semibold text-sm text-slate-800">{item.item_name}</p>
                    <p className="text-xs text-slate-400">{item.item_code} • {item.unit}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Received: {item.received_qty} • Rate: ₹{item.rate}
                    </p>
                  </div>
                  <div className="text-right">
                    <label className="text-xs text-slate-500 block mb-1">Return Qty</label>
                    <input
                      type="number" min="0" max={item.received_qty}
                      value={item.return_qty}
                      onChange={e => updateReturnQty(i, e.target.value)}
                      className={`w-20 border rounded-xl px-2 py-1.5 text-right text-sm outline-none focus:ring-2 ${
                        item.return_qty > item.received_qty
                          ? "border-red-300 focus:ring-red-300"
                          : "border-slate-200 focus:ring-purple-300"
                      }`}
                    />
                    {item.return_qty > 0 && (
                      <p className="text-xs text-purple-600 mt-1">
                        ₹{(item.return_qty * item.rate).toLocaleString("en-IN")}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Total */}
          {totalReturnAmount > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100 flex justify-between">
              <span className="text-sm font-semibold text-slate-600">Total Return Value</span>
              <span className="text-sm font-bold text-purple-600">
                ₹{totalReturnAmount.toLocaleString("en-IN")}
              </span>
            </div>
          )}
        </div>
      )}

      {/* REASON + NOTES */}
      {selectedPO && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mb-4 space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-2 block">
              Return Reason *
            </label>
            <select
              value={reason}
              onChange={e => setReason(e.target.value)}
              className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-purple-400 bg-white">
              <option value="">— Select Reason —</option>
              {RETURN_REASONS.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-2 block">
              Notes (optional)
            </label>
            <textarea
              rows={2} placeholder="Additional details..."
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="w-full border border-slate-200 rounded-xl p-3 text-sm resize-none outline-none focus:ring-2 focus:ring-purple-400"
            />
          </div>
        </div>
      )}

      {/* SAVE */}
      {selectedPO && (
        <button
          onClick={handleSave}
          disabled={loading}
          className="w-full py-3 rounded-2xl bg-purple-600 hover:bg-purple-700 text-white font-semibold text-sm transition disabled:opacity-40">
          {loading ? "Saving..." : "✓ Save Vendor Return & Update Inventory"}
        </button>
      )}
    </div>
  );
}